import type { CSSProperties } from 'react';
import { Box } from '../../atoms/Box';

export type NotificationStripItem = {
  id: string;
  message: string;
  type: 'INFO' | 'WARNING' | 'CRITICAL' | 'MAINTENANCE' | string;
  priority?: number;
};

const TYPE_STYLES: Record<string, { bg: string; color: string }> = {
  INFO: { bg: '#1c7ed6', color: '#fff' },
  WARNING: { bg: '#f08c00', color: '#fff' },
  CRITICAL: { bg: '#e03131', color: '#fff' },
  MAINTENANCE: { bg: '#7048e8', color: '#fff' },
};

const PRIORITY_RANK: Record<string, number> = {
  CRITICAL: 100,
  WARNING: 50,
  MAINTENANCE: 40,
  INFO: 10,
};

export type NotificationStripProps = {
  notifications: NotificationStripItem[];
};

/**
 * Full-width news ticker. Text always scrolls right → left.
 */
export function NotificationStrip({ notifications }: NotificationStripProps) {
  if (!notifications.length) return null;

  const dominant = [...notifications].sort(
    (a, b) =>
      (b.priority ?? PRIORITY_RANK[b.type] ?? 0) -
      (a.priority ?? PRIORITY_RANK[a.type] ?? 0),
  )[0];
  const palette = TYPE_STYLES[dominant.type] ?? TYPE_STYLES.INFO;
  const text = notifications.map((n) => n.message).join('  ·  ');
  // Duration scales with length so long messages stay readable
  const durationSec = Math.max(18, Math.min(60, Math.round(text.length / 3)));

  const barStyle: CSSProperties = {
    width: '100%',
    height: 36,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    background: palette.bg,
    color: palette.color,
    zIndex: 60,
    position: 'relative',
  };

  return (
    <Box style={barStyle} className="vipso-notification-strip" role="status">
      <style>{`
        @keyframes vipso-notif-marquee-rtl {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .vipso-notification-strip:hover .vipso-notif-track {
          animation-play-state: paused;
        }
        .vipso-notif-track {
          display: inline-flex;
          width: max-content;
          white-space: nowrap;
          will-change: transform;
          animation: vipso-notif-marquee-rtl ${durationSec}s linear infinite;
        }
        .vipso-notif-chunk {
          display: inline-block;
          padding: 0 64px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.01em;
          line-height: 36px;
        }
      `}</style>
      <Box className="vipso-notif-track">
        <span className="vipso-notif-chunk">{text}</span>
        <span className="vipso-notif-chunk" aria-hidden>
          {text}
        </span>
      </Box>
    </Box>
  );
}
